/**
 * ScheduleForm / SchedulePopover — configure a time-of-day + day-of-week
 * schedule that auto-triggers "Run All Jobs" for a pipeline.
 *
 * `ScheduleForm` is the reusable form itself (pipeline-schedule state,
 * cron building/parsing, save). It's rendered standalone by the
 * Scheduler tab (see SchedulerTab.tsx), where the user first picks a
 * pipeline from a list, then configures its schedule inline on the page.
 *
 * `SchedulePopover` is a thin floating-panel wrapper around `ScheduleForm`
 * kept for callers that want the old popover presentation (closes on
 * Escape or click-outside, matches NLToSQLPopover.tsx's shell).
 *
 * The backend only understands a standard 5-field cron expression — this
 * component builds one from the time + day picker (`buildCronExpression`)
 * and, when editing an existing schedule, best-effort parses the stored
 * cron back into picker state (`parseCronExpression`). Cron expressions
 * outside that shape (anything other than a fixed minute/hour on "*"
 * day-of-month/month) can't be round-tripped into the picker; those are
 * flagged to the user instead of silently discarded.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Clock, X } from "lucide-react";
import {
  getPipeline,
  updatePipelineSchedule,
  listJobs,
} from "../../features/pipeline-engineer/api/pe-client";
import type { RunMode, PEJob } from "../../features/pipeline-engineer/types";
import { JobDependencyGraph } from "./JobDependencyGraph";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

const LABEL_CLASS = "block text-xs font-medium text-white/60 mb-1";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ALL_DAYS = new Set([0, 1, 2, 3, 4, 5, 6]);

/** Zero-pad a non-negative integer to 2 digits, e.g. 9 -> "09". */
function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

/** Build a 5-field cron expression from a time-of-day + selected weekdays. */
function buildCronExpression(
  hour: number,
  minute: number,
  days: Set<number>,
): string {
  const dow =
    days.size === 7 || days.size === 0 ? "*" : [...days].sort().join(",");
  return `${minute} ${hour} * * ${dow}`;
}

/**
 * Best-effort parse of a stored cron expression into picker state.
 * Returns null when the expression uses a shape the picker can't represent
 * (e.g. step values, a restricted day-of-month/month, or non-numeric
 * minute/hour fields) rather than silently guessing.
 */
function parseCronExpression(
  cron: string,
): { hour: number; minute: number; days: Set<number> } | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minuteStr, hourStr, dom, month, dow] = parts;
  if (dom !== "*" || month !== "*") return null;
  if (!/^\d{1,2}$/.test(minuteStr) || !/^\d{1,2}$/.test(hourStr)) return null;
  const minute = Number(minuteStr);
  const hour = Number(hourStr);
  if (minute > 59 || hour > 23) return null;

  if (dow === "*") return { hour, minute, days: new Set(ALL_DAYS) };
  const dayNums = dow.split(",").map((d) => Number(d));
  if (dayNums.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) return null;
  return { hour, minute, days: new Set(dayNums) };
}

/** True when `value` parses to a real Date (guards against "Invalid Date" rendering). */
function isValidDateString(value: string | null): value is string {
  return value !== null && !Number.isNaN(new Date(value).getTime());
}

/**
 * The browser's own IANA timezone (e.g. "America/New_York"), falling back to
 * "UTC" if detection fails. The `Run time` picker always captures a local
 * wall-clock hour/minute, so the timezone sent alongside it must match —
 * otherwise the backend interprets that hour as UTC, which can silently
 * shift (and sometimes skip) "today" from the user's point of view.
 */
function getDefaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Common fixed-offset US/UK/EU abbreviations map to DST-aware IANA zones.
 * `ZoneInfo("EST")` etc. resolve on the backend, but as a FIXED UTC-5
 * offset year-round — during daylight saving (when the real local clock is
 * actually EDT, UTC-4) that silently disagrees with the wall clock by an
 * hour. Normalizing to the IANA region name (which observes DST correctly)
 * before saving avoids that drift entirely.
 */
const TIMEZONE_ABBREVIATION_ALIASES: Record<string, string> = {
  EST: "America/New_York",
  EDT: "America/New_York",
  CST: "America/Chicago",
  CDT: "America/Chicago",
  MST: "America/Denver",
  MDT: "America/Denver",
  PST: "America/Los_Angeles",
  PDT: "America/Los_Angeles",
  GMT: "UTC",
  BST: "Europe/London",
  CET: "Europe/Paris",
  CEST: "Europe/Paris",
};

/** Resolve a user-typed timezone to a DST-aware IANA name where ambiguous. */
function normalizeTimezone(value: string): string {
  const trimmed = value.trim();
  return TIMEZONE_ABBREVIATION_ALIASES[trimmed.toUpperCase()] ?? trimmed;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ScheduleFormProps {
  pipelineId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** The schedule-configuration form itself, with no positioning/shell. */
export function ScheduleForm({ pipelineId }: ScheduleFormProps) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [persistedEnabled, setPersistedEnabled] = useState(false);
  const [hour, setHour] = useState(2);
  const [minute, setMinute] = useState(0);
  const [days, setDays] = useState<Set<number>>(new Set(ALL_DAYS));
  const [unsupportedCron, setUnsupportedCron] = useState<string | null>(null);
  const [timezone, setTimezone] = useState(getDefaultTimezone);
  const [runMode, setRunMode] = useState<RunMode>("parallel");
  const [nextRunAt, setNextRunAt] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // Jobs for this pipeline — only needed to render the dependency graph
  // when "Custom Order" is the selected run mode.
  const [jobs, setJobs] = useState<PEJob[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listJobs(pipelineId);
        if (!cancelled) setJobs(data);
      } catch {
        // Best-effort — the graph simply doesn't render if jobs fail to load;
        // the schedule form itself surfaces its own load error separately.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pipelineId]);

  const handleJobDependsOnChange = useCallback(
    (jobId: string, dependsOn: string[]) => {
      setJobs((prev) =>
        prev.map((j) => (j.id === jobId ? { ...j, dependsOn } : j)),
      );
    },
    [],
  );

  // Pre-populate from the pipeline's current schedule config.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const pipeline = await getPipeline(pipelineId);
        if (cancelled) return;
        setEnabled(pipeline.scheduleEnabled);
        setPersistedEnabled(pipeline.scheduleEnabled);
        setTimezone(
          normalizeTimezone(pipeline.scheduleTimezone || getDefaultTimezone()),
        );
        setRunMode(pipeline.scheduleRunMode || "parallel");
        setNextRunAt(pipeline.scheduleNextRunAt);

        const cron = pipeline.scheduleCron;
        if (cron) {
          const parsed = parseCronExpression(cron);
          if (parsed) {
            setHour(parsed.hour);
            setMinute(parsed.minute);
            setDays(parsed.days);
            setUnsupportedCron(null);
          } else {
            setUnsupportedCron(cron);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err instanceof Error ? err.message : "Failed to load schedule.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pipelineId]);

  const timeValue = useMemo(
    () => `${pad2(hour)}:${pad2(minute)}`,
    [hour, minute],
  );

  const toggleDay = useCallback((day: number) => {
    setUnsupportedCron(null);
    setDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (
      enabled &&
      !persistedEnabled &&
      !window.confirm(
        "Enable automatic runs for this pipeline? Scheduled Full Replace jobs will replace all rows in their target tables.",
      )
    ) {
      return;
    }
    setSaveError(null);
    setSaveMsg(null);
    if (enabled && days.size === 0) {
      setSaveError("Select at least one day of the week.");
      return;
    }
    setSaving(true);
    try {
      const cronExpression = buildCronExpression(hour, minute, days);
      const resolvedTimezone =
        normalizeTimezone(timezone) || getDefaultTimezone();
      const updated = await updatePipelineSchedule(pipelineId, {
        enabled,
        cron_expression: enabled ? cronExpression : null,
        timezone: resolvedTimezone,
        run_mode: runMode,
      });
      setUnsupportedCron(null);
      setPersistedEnabled(enabled);
      setTimezone(resolvedTimezone);
      setNextRunAt(updated.scheduleNextRunAt);
      setSaveMsg(
        enabled
          ? `Schedule saved. Next run: ${
              isValidDateString(updated.scheduleNextRunAt)
                ? new Date(updated.scheduleNextRunAt).toLocaleString()
                : "pending"
            }`
          : "Schedule disabled.",
      );
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to save schedule.",
      );
    } finally {
      setSaving(false);
    }
  }, [
    pipelineId,
    enabled,
    persistedEnabled,
    hour,
    minute,
    days,
    timezone,
    runMode,
  ]);

  return (
    <div className="space-y-3">
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={18} className="animate-spin text-white/30" />
        </div>
      ) : loadError !== null ? (
        <p className="text-red-400 text-xs" role="alert">
          {loadError}
        </p>
      ) : (
        <>
          {/* Enable toggle */}
          <label className="flex items-center gap-2 text-xs text-white/70">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="rounded border-white/20 bg-white/5"
            />
            Automatically run all jobs on a schedule
          </label>

          {/* Time of day */}
          <div>
            <label htmlFor="schedule-time" className={LABEL_CLASS}>
              Run time
            </label>
            <input
              id="schedule-time"
              type="time"
              value={timeValue}
              onChange={(e) => {
                const [h, m] = e.target.value.split(":").map(Number);
                if (Number.isInteger(h) && Number.isInteger(m)) {
                  setHour(h);
                  setMinute(m);
                  setUnsupportedCron(null);
                }
              }}
              disabled={!enabled}
              className={`${INPUT_CLASS} disabled:opacity-40`}
            />
          </div>

          {/* Days of week */}
          <div>
            <label className={LABEL_CLASS}>Repeat on</label>
            <div className="flex gap-1">
              {DAY_LABELS.map((label, i) => {
                const selected = days.has(i);
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => toggleDay(i)}
                    disabled={!enabled}
                    aria-pressed={selected}
                    aria-label={label}
                    title={label}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-40 ${
                      selected
                        ? "bg-brand-500/30 border-brand-500/50 text-brand-200"
                        : "bg-white/5 border-white/10 text-white/50 hover:bg-white/10"
                    }`}
                  >
                    {label[0]}
                  </button>
                );
              })}
            </div>
            <p className="text-white/30 text-xs mt-1">
              {days.size === 7 || days.size === 0
                ? "Every day"
                : [...days]
                    .sort()
                    .map((d) => DAY_LABELS[d])
                    .join(", ")}
            </p>
          </div>

          {unsupportedCron !== null && (
            <p className="text-amber-400 text-xs">
              Current schedule uses a custom cron pattern (
              <code className="font-mono">{unsupportedCron}</code>) not
              representable by this picker. Saving will replace it with the
              time/days selected above.
            </p>
          )}

          {/* Timezone */}
          <div>
            <label htmlFor="schedule-timezone" className={LABEL_CLASS}>
              Timezone
            </label>
            <input
              id="schedule-timezone"
              type="text"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={!enabled}
              placeholder="UTC"
              className={`${INPUT_CLASS} disabled:opacity-40`}
            />
            <p className="text-white/30 text-xs mt-1">
              Use a region name (e.g. "America/New_York"), not a fixed
              abbreviation like "EST" — those don't adjust for daylight saving
              and can shift the next run by an hour.
            </p>
          </div>

          {/* Run mode */}
          <div>
            <label htmlFor="schedule-run-mode" className={LABEL_CLASS}>
              Run mode for scheduled runs
            </label>
            <select
              id="schedule-run-mode"
              value={runMode}
              onChange={(e) => setRunMode(e.target.value as RunMode)}
              disabled={!enabled}
              className={`${INPUT_CLASS} disabled:opacity-40`}
            >
              <option value="parallel">Parallel</option>
              <option value="sequential">Sequential</option>
              <option value="custom">Custom Order</option>
            </select>
          </div>

          {/* Pipeline order — only relevant for Custom Order run mode */}
          {runMode === "custom" && jobs.length > 0 && (
            <div>
              <label className={LABEL_CLASS}>Pipeline order</label>
              <JobDependencyGraph
                pipelineId={pipelineId}
                jobs={jobs}
                onDependsOnChange={handleJobDependsOnChange}
              />
            </div>
          )}

          {enabled && isValidDateString(nextRunAt) && (
            <p className="text-white/40 text-xs">
              Next run: {new Date(nextRunAt).toLocaleString()}
            </p>
          )}

          {saveError !== null && (
            <p className="text-red-400 text-xs" role="alert">
              {saveError}
            </p>
          )}
          {saveMsg !== null && (
            <p className="text-emerald-400 text-xs">{saveMsg}</p>
          )}

          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="flex items-center gap-1.5 w-full px-3 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed justify-center"
          >
            {saving ? (
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            ) : (
              <Clock size={12} aria-hidden="true" />
            )}
            {saving ? "Saving…" : "Save Schedule"}
          </button>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Popover shell
// ---------------------------------------------------------------------------

interface SchedulePopoverProps {
  pipelineId: string;
  onClose: () => void;
}

/**
 * Floating-panel presentation of `ScheduleForm` (fixed-width panel, closes
 * on Escape or click-outside — matches NLToSQLPopover.tsx's shell). Kept
 * for callers that want the popover presentation; the Scheduler tab
 * renders `ScheduleForm` directly instead.
 */
export function SchedulePopover({ pipelineId, onClose }: SchedulePopoverProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Close on click-outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current !== null &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return (
    <div
      ref={containerRef}
      className="absolute z-50 right-0 top-full mt-1 w-96 bg-[#1a1f2e] border border-white/15 rounded-xl shadow-2xl p-4 space-y-3"
      role="dialog"
      aria-label="Configure pipeline schedule"
      aria-modal="false"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Clock size={13} className="text-brand-400" aria-hidden="true" />
          <span className="text-xs font-semibold text-white/80">
            Run Schedule
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close schedule popover"
          className="p-1 text-white/30 hover:text-white/60 transition-colors rounded"
        >
          <X size={12} aria-hidden="true" />
        </button>
      </div>
      <ScheduleForm pipelineId={pipelineId} />
    </div>
  );
}
